import { RetryTimeoutError, normalizeRetryError } from "./errors";
import { calculateBackoffDelay, mergeRetryPolicy } from "./policy";
import { emitRetryAttemptTelemetry } from "./telemetry";
import { isTransientError } from "./transientErrors";

function delay(ms, signal) {
  if (ms <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new DOMException("Aborted", "AbortError"));
    };

    if (signal?.aborted) {
      clearTimeout(timeoutId);
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

async function withTimeout(operation, timeoutMs, phase, operationId, createTimeoutError) {
  if (!timeoutMs || timeoutMs <= 0) {
    return operation();
  }

  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        createTimeoutError?.(phase, operationId) ??
          new RetryTimeoutError(phase, operationId)
      );
    }, timeoutMs);
  });

  try {
    return await Promise.race([operation(), timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export async function runWithRetry(operation, options) {
  const policy = mergeRetryPolicy(options.policy, options.basePolicy);
  const { phase, operationId, context = {}, signal } = options;
  let lastError;

  for (let attempt = 1; attempt <= policy.maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    try {
      const result = await withTimeout(
        () => operation(attempt),
        policy.timeoutMs,
        phase,
        operationId,
        options.createTimeoutError
      );
      return { result, attempts: attempt };
    } catch (error) {
      lastError = error;
      const isRetryable =
        attempt < policy.maxAttempts &&
        (options.shouldRetry?.(error, attempt) ?? isTransientError(error));

      if (!isRetryable) {
        emitRetryAttemptTelemetry(
          {
            operationId,
            phase,
            domain: context.domain,
            toolId: context.toolId,
          },
          {
            attempt,
            maxAttempts: policy.maxAttempts,
            delayMs: 0,
            error,
          },
          true
        );

        const normalized =
          options.normalizeError?.(error, phase, operationId, attempt) ??
          normalizeRetryError(error, phase, operationId, attempt);
        throw normalized;
      }

      const delayMs = calculateBackoffDelay(attempt, policy);
      const retryEvent = {
        attempt,
        maxAttempts: policy.maxAttempts,
        delayMs,
        error,
      };

      emitRetryAttemptTelemetry(
        {
          operationId,
          phase,
          domain: context.domain,
          toolId: context.toolId,
        },
        retryEvent,
        false
      );
      options.onRetry?.(retryEvent);
      await delay(delayMs, signal);
    }
  }

  const normalized =
    options.normalizeError?.(lastError, phase, operationId, policy.maxAttempts) ??
    normalizeRetryError(lastError, phase, operationId, policy.maxAttempts);
  throw normalized;
}
